import React, { useEffect, useState, useRef } from 'react';
import { View, Text, StyleSheet } from 'react-native';
// Relative path corrected: file lives at packages/ui/airlineLogos.json
import logos from '../../../airlineLogos.json';

export interface AirlineLogoProps {
  iata?: string;
  operator?: string; // human-readable name
  size?: number;
  style?: any;
}

const AirlineLogo: React.FC<AirlineLogoProps> = ({ iata, operator, size = 24, style }) => {
  const [src, setSrc] = useState<string | undefined>(undefined);
  const candidatesRef = useRef<string[]>([]);

  useEffect(() => {
    // Build candidate list in preferred order. We'll try rendering them directly
    // and rely on Image onError to fallback, because HEAD requests can be blocked
    // by some servers or dev setups.
    const candidates: string[] = [];
    if (iata) {
      const up = iata.toUpperCase();
      const low = iata.toLowerCase();
      // try common variants: with leading slash (public assets), package-local, and without leading slash
      candidates.push(`/assets/airlines/${up}.svg`);
      candidates.push(`/assets/airlines/${up}.png`);
      candidates.push(`/assets/airlines/${low}.svg`);
      candidates.push(`/assets/airlines/${low}.png`);
      candidates.push(`assets/airlines/${up}.svg`);
      candidates.push(`assets/airlines/${up}.png`);
      candidates.push(`assets/airlines/${low}.svg`);
      candidates.push(`assets/airlines/${low}.png`);
      candidates.push(`/packages/ui/assets/airlines/${up}.svg`);
      candidates.push(`/packages/ui/assets/airlines/${up}.png`);
      // also allow bare filename (user's network screenshot showed requests for "CX.png")
      candidates.push(`${up}.png`);
      candidates.push(`${up}.svg`);

      const entry = logos.find((e: any) => e.airline_iata && e.airline_iata.toLowerCase() === iata.toLowerCase());
      if (entry && entry.domain) candidates.push(`https://logo.clearbit.com/${entry.domain}`);
    }
    if ((!iata || candidates.length === 0) && operator) {
      const op = operator.toLowerCase();
      const found = logos.find((e: any) => (e.name && e.name.toLowerCase().includes(op)) || (e.domain && e.domain.toLowerCase().includes(op)) );
      if (found && found.domain) candidates.push(`https://logo.clearbit.com/${found.domain}`);
    }

    // pick the first candidate as initial src; Image onError will cycle through others
    if (candidates.length > 0) setSrc(candidates[0]);
    else setSrc(undefined);
    // store candidates in an instance ref to avoid cross-instance races
    candidatesRef.current = candidates;
    return () => { candidatesRef.current = []; };
  }, [iata, operator]);

  if (src) {
    // We'll render the image and on error try the next candidate from the stored list
    const handleError = () => {
      try {
        const candidates: string[] = candidatesRef.current || [];
        const idx = candidates.indexOf(src || '');
        const next = (idx >= 0 && idx < candidates.length - 1) ? candidates[idx + 1] : undefined;
        setSrc(next);
      } catch (e) {
        setSrc(undefined);
      }
    };

    // On web (react-native-web) some Image behaviors differ; render a native <img>
    // for more predictable results. Fallback to react-native Image otherwise.
    const isWeb = typeof document !== 'undefined' && typeof window !== 'undefined';
    if (isWeb) {
      // Inline style: prefer object-fit contain so svg/png scale nicely.
      const imgStyle: any = {
        width: typeof size === 'number' ? `${size}px` : size,
        height: typeof size === 'number' ? `${size}px` : size,
        objectFit: 'contain',
        verticalAlign: 'middle',
      };
      // If a simple style with marginRight was passed (from TransportCard), apply it.
      if (style && typeof style === 'object' && style.marginRight) imgStyle.marginRight = style.marginRight;
      return (
        // eslint-disable-next-line jsx-a11y/alt-text
        <img src={src} onError={handleError as any} style={imgStyle} />
      );
    }

    return (
      <img src={src} onError={handleError as any} style={{ width: size, height: size, objectFit: 'contain' }} />
    );
  }

  const text = iata || operator || '--';
  return (
    <View style={[styles.badge, { width: size + 6, height: size + 6 }, style]}>
      <Text style={[styles.text, { fontSize: Math.max(10, Math.floor(size / 2)) }]} numberOfLines={1}>{text}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  badge: {
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
  },
  text: {
    color: '#111827',
    fontWeight: '700',
  },
});

export default AirlineLogo;
